<template>
  <div class="ts-root" ref="root" :style="{ height: (STAGE_H * scale) + 'px' }">
    <div class="ts-stage" :style="{ transform: 'translateX(' + offsetX + 'px) scale(' + scale + ')' }">
      <!-- Broadcast side view of the court: one flat SVG where every court
           point goes through the same perspective projection (proj), so
           lines, boards, net and ball all recede together. -->
      <svg class="ts-scene" :viewBox="'0 0 ' + STAGE_W + ' ' + STAGE_H" preserveAspectRatio="none">
        <defs>
          <pattern v-if="crowd" :id="uid + 'tsCrowd'" patternUnits="userSpaceOnUse" width="90" height="45">
            <image :href="crowd" x="0" y="0" width="90" height="45" />
          </pattern>
          <linearGradient :id="uid + 'tsSurround'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" :stop-color="palette.surroundFar" />
            <stop offset="1" :stop-color="palette.surroundNear" />
          </linearGradient>
          <!-- Serve fan gradients, in the fan's own coordinates (apex at 0,0):
               full colour at the vertex, fading out toward the opening. -->
          <radialGradient :id="uid + 'tsServeFan'" cx="0" cy="0" :r="FAN_R" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="#8fd6ff" stop-opacity="0.45" />
            <stop offset="1" stop-color="#6cc3f5" stop-opacity="0" />
          </radialGradient>
          <radialGradient :id="uid + 'tsServePing'" cx="0" cy="0" :r="FAN_R" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="#b5e8ff" stop-opacity="0.95" />
            <stop offset="0.55" stop-color="#7fd0ff" stop-opacity="0.55" />
            <stop offset="1" stop-color="#6cc3f5" stop-opacity="0.05" />
          </radialGradient>
          <radialGradient :id="uid + 'tsBall'" cx="0.35" cy="0.35" r="0.7">
            <stop offset="0" stop-color="#f4ff7a" />
            <stop offset="1" stop-color="#b9c21a" />
          </radialGradient>
        </defs>

        <!-- Stands: crowd everywhere behind the boards. -->
        <rect x="0" y="0" :width="STAGE_W" :height="STAGE_H" fill="#1c2833" />
        <rect v-if="crowd" x="0" y="0" :width="STAGE_W" :height="STAGE_H" :fill="'url(#' + uid + 'tsCrowd' + ')'" opacity="0.8" />

        <!-- Run-off area around the court, then the dark foreground band. -->
        <polygon :points="surround" :fill="'url(#' + uid + 'tsSurround' + ')'" />
        <rect x="0" :y="FRONT_Y" :width="STAGE_W" :height="STAGE_H - FRONT_Y" :fill="palette.front" />
        <line x1="0" :y1="FRONT_Y" :x2="STAGE_W" :y2="FRONT_Y" stroke="rgba(255,255,255,0.35)" stroke-width="1" />

        <!-- Ad-boards: far one flat, side ones following the run-off edges. -->
        <ScrollingBoards :boards="boards" :uid="uid" :logo-url="logoUrl" :board-text="boardText" />

        <!-- Logos painted on the run-off along the near sideline, one per half
             (the net post is in the middle). -->
        <template v-if="showSirplayLogo"><image v-for="(t, i) in logoDecals" :key="'logo' + i" :href="PITCH_LOGO" :transform="t" x="-180" y="-50" width="360" height="100" preserveAspectRatio="none" class="arena-logo" /></template>

        <!-- Serve fan: from the server toward the cross-court service box
             (hidden while a point is being played). -->
        <!-- A faint fixed fan plus two "ping" waves opening out of the vertex
             one after the other, like a radar. -->
        <g v-if="fan && !rally" :transform="'translate(' + pt(fan.apex) + ')'">
          <path :d="fan.d" :fill="'url(#' + uid + 'tsServeFan' + ')'" />
          <g v-for="w in 2" :key="'ping' + w" opacity="0">
            <animateTransform attributeName="transform" type="scale" values="0.08;1" keyTimes="0;1" calcMode="spline" keySplines="0.2 0.7 0.4 1" dur="1.8s" :begin="(w - 1) * 0.9 + 's'" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0;1;0.9;0" keyTimes="0;0.12;0.7;1" dur="1.8s" :begin="(w - 1) * 0.9 + 's'" repeatCount="indefinite" />
            <path :d="fan.d" :fill="'url(#' + uid + 'tsServePing' + ')'" />
          </g>
        </g>

        <!-- Court lines. -->
        <g fill="none" stroke="rgba(255,255,255,0.92)" stroke-width="1.6" stroke-linejoin="miter">
          <path v-for="(d, i) in courtLines" :key="'l' + i" :d="d" />
        </g>

        <!-- Net seen edge-on from the side: white tape on the far half,
             the dark near post and its shadow on the near half. -->
        <line :x1="net.base[0].x + 2" :y1="net.base[0].y" :x2="net.base[1].x + 2" :y2="net.base[1].y" stroke="rgba(0,0,0,0.25)" stroke-width="3" />
        <line :x1="net.top.x" :y1="net.top.y" :x2="net.mid.x" :y2="net.mid.y" stroke="#fff" stroke-width="2" />
        <line :x1="net.mid.x" :y1="net.mid.y" :x2="net.base[1].x" :y2="net.base[1].y" stroke="#0a0a0a" stroke-width="2.6" />

        <!-- Ball, bouncing at the server's spot while waiting for the serve. -->
        <g v-if="ball && !rally && !finished">
          <ellipse :cx="ball.x + 1" :cy="ball.y + 5" rx="4" ry="1.5" fill="rgba(0,0,0,0.3)" class="ts-ball-idle-shadow" />
          <g class="ts-ball-idle">
            <circle :cx="ball.x" :cy="ball.y" r="4.6" :fill="'url(#' + uid + 'tsBall' + ')'" />
          </g>
        </g>

        <!-- Point being played: the ball flies between the two ends. -->
        <g v-if="rally">
          <ellipse v-for="b in rally.bounces" :key="b.key" :cx="b.x" :cy="b.y" rx="7" ry="2.4" class="ts-bounce" />
          <ellipse :cx="rally.shadow.x" :cy="rally.shadow.y" :rx="4 * rally.k" :ry="1.5 * rally.k" fill="rgba(0,0,0,0.35)" :opacity="rally.opacity" />
          <circle :cx="rally.ball.x" :cy="rally.ball.y" :r="4.6 * rally.k" :fill="'url(#' + uid + 'tsBall' + ')'" :opacity="rally.opacity" />
        </g>
      </svg>

      <!-- Flat overlays (HTML, so text renders crisply at every scale). -->
      <div class="ts-score">
        <span class="ts-score-pts">
          <span :key="'h' + popKey" :class="{ 'is-pop': popSide === '1' }">{{ points.h }}</span>
          <span :key="'a' + popKey" :class="{ 'is-pop': popSide === '2' }">{{ points.a }}</span>
        </span>
        <span v-if="statusText" class="ts-score-status">{{ statusText }}</span>
      </div>
      <div class="ts-label ts-label--home" :class="{ 'is-low': labelLow.home }">{{ homeName }}</div>
      <div class="ts-label ts-label--away" :class="{ 'is-low': labelLow.away }">{{ awayName }}</div>
      <div v-if="(servingSide === '1' || servingSide === '2') && !finished" class="ts-serving">
        <span class="ts-serving-label">{{ $t('statistics.atserve') }}</span>
        <span class="ts-serving-name">{{ servingSide === '1' ? homeName : awayName }}</span>
        <span class="ts-serving-bar" :class="servingSide === '1' ? 'is-home' : 'is-away'"></span>
      </div>

      <!-- Point / game won: full-stage overlay, then the score box updates. -->
      <transition name="ts-overlay">
        <div v-if="overlay" :key="overlay.key" class="ts-point-overlay" :style="{ '--side-color': overlay.color }">
          <div class="ts-point-title">{{ overlay.title }}</div>
          <div v-if="overlay.sub" class="ts-point-sub">{{ overlay.sub }}</div>
          <div class="ts-point-team">{{ overlay.name }}</div>
          <div class="ts-point-score">
            <span :class="{ 'is-won': overlay.side === '1' }">{{ overlay.h }}</span>
            <span class="ts-point-sep">-</span>
            <span :class="{ 'is-won': overlay.side === '2' }">{{ overlay.a }}</span>
          </div>
        </div>
      </transition>

      <!-- Match over: big badge that stays up. -->
      <transition name="ts-final">
        <div v-if="finished" class="ts-final">
          <div class="ts-final-badge">
            <svg class="ts-final-icon" viewBox="0 0 40 40">
              <path d="M12,5 H28 V15 a8,8 0 0 1 -16,0 Z" fill="#facc15" />
              <path d="M12,8 H6 a6,6 0 0 0 6,9 M28,8 H34 a6,6 0 0 1 -6,9" fill="none" stroke="#facc15" stroke-width="2.4" />
              <rect x="18" y="22" width="4" height="7" fill="#eab308" />
              <rect x="12" y="29" width="16" height="5" rx="1.5" fill="#eab308" />
            </svg>
            <div class="ts-final-title">{{ $t('statistics.matchfinished') }}</div>
            <div v-if="winnerName" class="ts-final-winner">{{ winnerName }}</div>
            <div v-if="sets.length" class="ts-final-sets">
              <span v-for="(st, i) in sets" :key="'fs' + i">{{ st.h }}-{{ st.a }}</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ScrollingBoards from '@/components/arenamatchtracker/ScrollingBoards.vue';
import { makeProjection, pt, seg, crowdTexture, makeBoard, stageScaleMixin, floorLogo, PITCH_LOGO, sirplayLicenceMixin } from '@/services/matchtracker/broadcastScene';

// Stage = fixed broadcast frame, scaled to the container width and zoomed
// in by ZOOM (the extra width is cropped equally off both sides, where
// there are only stands and boards).
const ZOOM = 1.1;
const STAGE_W = 460;
const STAGE_H = 270;
// Court projection (see makeProjection).
const FAR_Y = 52;
const NEAR_Y = 197;
const FAR_W = 328;
const NEAR_W = 448;
const FAR_CX = 228;
const NEAR_CX = 230;
const FRONT_Y = 214;
const FAN_R = 145;
// Run-off / ad-board corners in screen px.
const FAR_L = [86, 37];
const FAR_R = [376, 37];
const SIDE_L = [0, 108];
const SIDE_R = [STAGE_W, 102];
// Court proportions (ITF): 23.77m x 10.97m doubles, 8.23m singles, service line 6.40m from the net.
const SINGLES_V = (10.97 - 8.23) / 2 / 10.97;
const SERVICE_U = 6.40 / 23.77;

const POINT_INDEX = { '0': 0, '15': 1, '30': 2, '40': 3, 'A': 4, 'AD': 4, 'ADV': 4, '50': 4 };

// Rally timing (ms).
const T_TOSS = 450;
const T_SERVE = 650;
const T_SHOT = 720;
const T_HOP = 340;
const T_OUT = 600;
const OVERLAY_MS = 2600;

const rand = (a, b) => a + Math.random() * (b - a);
const pick = list => list[Math.floor(Math.random() * list.length)];

const PALETTES = {
  'f-hard': { surroundFar: '#3a90c6', surroundNear: '#2a74a8', front: '#245c86' },
  'f-clay': { surroundFar: '#c8693a', surroundNear: '#a9532b', front: '#7e3d20' },
  'f-grass': { surroundFar: '#4f9a44', surroundNear: '#3c7f34', front: '#2c5f27' }
};

const { proj } = makeProjection({ farY: FAR_Y, nearY: NEAR_Y, farW: FAR_W, nearW: NEAR_W, farCx: FAR_CX, nearCx: NEAR_CX });

export default {
  name: 'TennisStadium',
  components: { ScrollingBoards },
  mixins: [stageScaleMixin, sirplayLicenceMixin],
  props: {
    homeName: { type: String, default: '' },
    awayName: { type: String, default: '' },
    // Current game points as the feed sends them ("0"/"15"/"30"/"40"/"A", or tie-break numbers).
    pointsH: { type: String, default: '0' },
    pointsA: { type: String, default: '0' },
    // '1' home / '2' away / null.
    servingSide: { type: String, default: null },
    isTiebreak: { type: Boolean, default: false },
    // f-hard | f-clay | f-grass
    surface: { type: String, default: 'f-hard' },
    logoUrl: { type: String, default: '' },
    boardText: { type: String, default: 'LIVE' },
    homeColor: { type: String, default: '' },
    awayColor: { type: String, default: '' },
    // Games per set so far, [{ h, a }] — tells a game win from a point win.
    sets: { type: Array, default: () => [] },
    // Aces so far (stat 20), { h, a }: a point that comes with one more ace
    // for the server is played as an ace.
    aces: { type: Object, default: () => ({ h: 0, a: 0 }) },
    finished: { type: Boolean, default: false }
  },
  data() {
    return {
      PITCH_LOGO,
      STAGE_W,
      STAGE_H,
      ZOOM,
      FRONT_Y,
      FAN_R,
      crowd: '',
      // Score shown on the court: lags the props while a point is animated.
      curH: this.pointsH,
      curA: this.pointsA,
      curServe: this.servingSide,
      rally: null,
      rallyRaf: null,
      overlay: null,
      overlayTimer: null,
      popSide: null,
      popKey: 0,
      lastGames: null,
      // Ace totals already accounted for by a shown point.
      seenAces: { h: parseInt(this.aces.h) || 0, a: parseInt(this.aces.a) || 0 }
    };
  },
  watch: {
    pointKey(val, old) {
      if (!old || this.finished) {
        this.syncScore();
        return;
      }
      this.onScoreChange();
    },
    // A serve change with no point played (e.g. first feed update).
    servingSide(val) {
      if (!this.rally) this.curServe = val;
    },
    finished(val) {
      if (val) {
        this.stopRally();
        this.syncScore();
      }
    }
  },
  mounted() {
    this.crowd = crowdTexture();
  },
  created() {
    this.lastGames = this.gamesTotal;
  },
  beforeDestroy() {
    this.stopRally();
    clearTimeout(this.overlayTimer);
  },
  methods: {
    pt,
    syncScore() {
      this.curH = this.pointsH;
      this.curA = this.pointsA;
      this.curServe = this.servingSide;
      this.lastGames = this.gamesTotal;
      this.syncAces();
    },
    syncAces() {
      this.seenAces = { h: parseInt(this.aces.h) || 0, a: parseInt(this.aces.a) || 0 };
    },
    // True if the server's aces went up with this point, which the server won.
    isAce(server, winner) {
      const key = server === '1' ? 'h' : 'a';
      const up = (parseInt(this.aces[key]) || 0) > this.seenAces[key];
      this.syncAces();
      return up && winner === server;
    },
    idx(v, tiebreak) {
      return tiebreak ? (parseInt(v) || 0) : POINT_INDEX[String(v).toUpperCase()];
    },
    // Works out who won the point from the old/new score, plays the rally,
    // then shows the overlay and moves the score box on.
    onScoreChange() {
      const games = this.gamesTotal;
      const prevGames = this.lastGames || { h: 0, a: 0 };
      let winner = null;
      let kind = 'point';
      if (games.h !== prevGames.h || games.a !== prevGames.a) {
        kind = 'game';
        winner = games.h > prevGames.h ? '1' : (games.a > prevGames.a ? '2' : null);
      } else {
        const tb = this.tiebreak;
        const o = { h: this.idx(this.curH, tb), a: this.idx(this.curA, tb) };
        const n = { h: this.idx(this.pointsH, tb), a: this.idx(this.pointsA, tb) };
        if ([o.h, o.a, n.h, n.a].some(v => v === undefined)) winner = null;
        // Scored, or took the opponent's advantage away (A-40 -> 40-40).
        else if (n.h > o.h || n.a < o.a) winner = '1';
        else if (n.a > o.a || n.h < o.h) winner = '2';
      }
      if (!winner) {
        this.syncScore();
        return;
      }
      const server = this.curServe === '1' || this.curServe === '2' ? this.curServe : winner;
      const ace = this.isAce(server, winner);
      this.playRally(server, winner, ace, () => this.showPoint(kind, winner, ace));
    },
    showPoint(kind, winner, ace) {
      this.syncScore();
      const cur = this.sets.length ? this.sets[this.sets.length - 1] : null;
      const h = kind === 'game' ? (cur ? cur.h : this.gamesTotal.h) : this.points.h;
      const a = kind === 'game' ? (cur ? cur.a : this.gamesTotal.a) : this.points.a;
      this.popKey++;
      this.popSide = winner;
      this.overlay = {
        key: this.popKey,
        side: winner,
        // An ace is announced as such, even when it wins the game (then
        // "Game" goes underneath).
        title: this.$t(ace ? 'statistics.ace' : (kind === 'game' ? 'statistics.game' : 'statistics.point')),
        sub: ace && kind === 'game' ? this.$t('statistics.game') : '',
        name: winner === '1' ? this.homeName : this.awayName,
        color: this.sideColor(winner),
        h,
        a
      };
      clearTimeout(this.overlayTimer);
      this.overlayTimer = setTimeout(() => { this.overlay = null; }, OVERLAY_MS);
    },
    sideColor(side) {
      if (side === '1') return this.homeColor || '#2d5fae';
      return this.awayColor || '#1c8a8e';
    },
    stopRally() {
      if (this.rallyRaf) cancelAnimationFrame(this.rallyRaf);
      this.rallyRaf = null;
      this.rally = null;
    },
    // Builds the flight segments of a rally that the winner closes out.
    buildRally(server, winner, ace) {
      const other = s => (s === '1' ? '2' : '1');
      // Shot i is hit by the server when i is even: the winner must hit last.
      // An ace: only the serve, faster and flatter, never returned.
      const counts = winner === server ? [1, 3, 3, 5, 5, 7] : [2, 2, 4, 4, 6];
      const n = ace ? 1 : pick(counts);
      const spot = this.serveSpot;
      const serveV = spot ? spot.v : 0.5;
      const hitter = side => ({ u: side === '1' ? rand(-0.05, 0.05) : rand(0.95, 1.05), v: rand(0.2, 0.8), h: 11 });
      const segs = [];
      let from = { u: spot ? spot.u : (server === '1' ? -0.012 : 1.012), v: serveV, h: 0 };
      // Toss: straight up above the server.
      segs.push({ from, to: { ...from, h: 26 }, peak: 0, dur: T_TOSS, ease: true });
      from = { ...from, h: 26 };
      let side = server;
      for (let i = 0; i < n; i++) {
        const last = i === n - 1;
        let bounce;
        if (i === 0) {
          // Serve: into the cross-court service box.
          const bu = side === '1' ? rand(0.55, 0.5 + SERVICE_U - 0.02) : rand(0.5 - SERVICE_U + 0.02, 0.45);
          const bv = serveV < 0.5 ? rand(0.55, 0.82) : rand(0.18, 0.45);
          bounce = { u: bu, v: bv, h: 0 };
        } else {
          bounce = { u: side === '1' ? rand(0.62, 0.92) : rand(0.08, 0.38), v: rand(0.18, 0.82), h: 0 };
        }
        const serveDur = ace ? T_SERVE * 0.7 : T_SERVE;
        segs.push({ from, to: bounce, peak: i === 0 ? (ace ? 5 : 8) : rand(18, 30), dur: i === 0 ? serveDur : T_SHOT, bounce: true });
        if (last) {
          // Winner: the ball skips past the opponent and out of the court.
          const du = bounce.u - from.u;
          const dv = bounce.v - from.v;
          const reach = ace ? 0.8 : 0.55;
          const out = { u: bounce.u + du * reach, v: Math.max(-0.1, Math.min(1.1, bounce.v + dv * reach)), h: 3 };
          segs.push({ from: bounce, to: out, peak: ace ? 9 : 12, dur: ace ? T_OUT * 0.8 : T_OUT, fade: true });
        } else {
          const next = hitter(other(side));
          segs.push({ from: bounce, to: next, peak: 12, dur: T_HOP });
          from = next;
        }
        side = other(side);
      }
      return segs;
    },
    playRally(server, winner, ace, done) {
      this.stopRally();
      const segs = this.buildRally(server, winner, ace);
      let i = 0;
      let t0 = null;
      const bounces = [];
      const frame = ts => {
        if (t0 === null) t0 = ts;
        let seg = segs[i];
        let t = (ts - t0) / seg.dur;
        while (t >= 1) {
          if (seg.bounce) {
            const b = proj(seg.to.u, seg.to.v);
            bounces.push({ key: ts + '-' + i, x: b.x, y: b.y });
            if (bounces.length > 3) bounces.shift();
          }
          i++;
          if (i >= segs.length) {
            this.stopRally();
            done();
            return;
          }
          t0 += seg.dur;
          seg = segs[i];
          t = (ts - t0) / seg.dur;
        }
        const e = seg.ease ? 1 - (1 - t) * (1 - t) : t;
        const u = seg.from.u + (seg.to.u - seg.from.u) * t;
        const v = seg.from.v + (seg.to.v - seg.from.v) * t;
        const h = seg.from.h + (seg.to.h - seg.from.h) * e + 4 * seg.peak * t * (1 - t);
        const g = proj(u, v);
        const k = g.w / FAR_W;
        this.rally = {
          ball: { x: g.x, y: g.y - 3 - h * k },
          shadow: { x: g.x + 1, y: g.y + 2 },
          k,
          opacity: seg.fade ? 1 - t : 1,
          bounces: bounces.slice()
        };
        this.rallyRaf = requestAnimationFrame(frame);
      };
      this.rallyRaf = requestAnimationFrame(frame);
    }
  },
  computed: {
    logoDecals() {
      return [0.25, 0.75].map(u => floorLogo(proj, u, 1 + 0.5 / 10.97, 0.36 / 10.97));
    },
    // SVG ids are document-global: keep each instance's gradients apart.
    uid() { return 'ts' + this._uid + '-'; },
    palette() { return PALETTES[this.surface] || PALETTES['f-hard']; },
    pointKey() {
      const g = this.gamesTotal;
      return [this.pointsH, this.pointsA, g.h, g.a].join('|');
    },
    gamesTotal() {
      return this.sets.reduce((acc, st) => ({ h: acc.h + (parseInt(st.h) || 0), a: acc.a + (parseInt(st.a) || 0) }), { h: 0, a: 0 });
    },
    winnerName() {
      let h = 0;
      let a = 0;
      this.sets.forEach(st => {
        const sh = parseInt(st.h) || 0;
        const sa = parseInt(st.a) || 0;
        if (sh > sa) h++;
        else if (sa > sh) a++;
      });
      if (h === a) return '';
      return h > a ? this.homeName : this.awayName;
    },
    tiebreak() {
      if (this.isTiebreak) return true;
      const known = v => POINT_INDEX[String(v).toUpperCase()] !== undefined;
      return !known(this.pointsH) || !known(this.pointsA);
    },
    // Points as indexes (0, 1, 2, 3, 4 = advantage), or raw counts in a tie-break.
    pointIdx() {
      return { h: this.idx(this.curH, this.tiebreak), a: this.idx(this.curA, this.tiebreak) };
    },
    points() {
      const show = v => (String(v).toUpperCase() === '50' || String(v).toUpperCase() === 'ADV' || String(v).toUpperCase() === 'AD') ? 'A' : String(v);
      return { h: show(this.curH), a: show(this.curA) };
    },
    statusText() {
      if (this.tiebreak) return '';
      const { h, a } = this.pointIdx;
      if (h >= 3 && a >= 3 && h === a) return this.$t('statistics.deuce');
      if (this.curServe !== '1' && this.curServe !== '2') return '';
      const srv = this.curServe === '1' ? h : a;
      const rcv = this.curServe === '1' ? a : h;
      if (rcv >= 3 && rcv > srv) return this.$t('statistics.breakpoint');
      if (srv === 4) return this.$t('statistics.advantage');
      return '';
    },
    // The run-off area and boards are drawn with a wider lens than the court
    // itself (like the broadcast frame), so they use fixed screen corners.
    surround() {
      return [FAR_L, FAR_R, SIDE_R, [STAGE_W, FRONT_Y + 10], [0, FRONT_Y + 10], SIDE_L].map(p => p.join(',')).join(' ');
    },
    boards() {
      const P = p => ({ x: p[0], y: p[1] });
      return [
        makeBoard('far', P(FAR_L), P(FAR_R), 15, 15, 5, 40),
        makeBoard('left', P(SIDE_L), P(FAR_L), 24, 15, 2, 38),
        makeBoard('right', P(FAR_R), P(SIDE_R), 15, 24, 2, 38)
      ];
    },
    courtLines() {
      const L = [];
      // Doubles and singles outlines.
      L.push('M' + [proj(0, 0), proj(1, 0), proj(1, 1), proj(0, 1)].map(pt).join(' L') + ' Z');
      L.push(seg(proj(0, SINGLES_V), proj(1, SINGLES_V)));
      L.push(seg(proj(0, 1 - SINGLES_V), proj(1, 1 - SINGLES_V)));
      // Service lines and centre service line.
      const u1 = 0.5 - SERVICE_U;
      const u2 = 0.5 + SERVICE_U;
      L.push(seg(proj(u1, SINGLES_V), proj(u1, 1 - SINGLES_V)));
      L.push(seg(proj(u2, SINGLES_V), proj(u2, 1 - SINGLES_V)));
      L.push(seg(proj(u1, 0.5), proj(u2, 0.5)));
      // Centre marks on the baselines.
      L.push(seg(proj(0, 0.5), proj(0.012, 0.5)));
      L.push(seg(proj(1, 0.5), proj(0.988, 0.5)));
      return L;
    },
    net() {
      const far = proj(0.5, -0.08);
      const near = proj(0.5, 1.08);
      const mid = proj(0.5, 0.62);
      return {
        base: [far, near],
        top: { x: far.x, y: 30 },
        mid: { x: mid.x, y: mid.y - 12 }
      };
    },
    // Where the server stands: behind their baseline, on the deuce side
    // after an even number of points and on the ad side after an odd one.
    serveSpot() {
      if (this.curServe !== '1' && this.curServe !== '2') return null;
      const total = (this.pointIdx.h || 0) + (this.pointIdx.a || 0);
      const odd = total % 2 === 1;
      const home = this.curServe === '1';
      // Home serves from the left end facing right: their right hand (deuce)
      // is the near side. Away is mirrored.
      const nearSide = home ? !odd : odd;
      const v = nearSide ? 1 - SINGLES_V - 0.03 : SINGLES_V + 0.03;
      const u = home ? -0.012 : 1.012;
      // Cross-court target: the receiver's service box on the other side.
      const tu = home ? 0.5 + SERVICE_U * 0.6 : 0.5 - SERVICE_U * 0.6;
      const tv = nearSide ? 0.32 : 0.68;
      return { u, v, at: proj(u, v), target: proj(tu, tv) };
    },
    ball() {
      if (!this.serveSpot) return null;
      const p = this.serveSpot.at;
      return { x: p.x, y: p.y - 3 };
    },
    // A label drops lower when its team serves from the far side, so it
    // doesn't hide the ball (which bounces up to ~10px above its spot).
    labelLow() {
      const b = this.ball;
      const far = !!b && !this.finished && b.y < 110;
      return { home: far && this.curServe === '1', away: far && this.curServe === '2' };
    },
    fan() {
      if (!this.serveSpot) return null;
      const apex = this.serveSpot.at;
      const t = this.serveSpot.target;
      const c = Math.atan2(t.y - apex.y, t.x - apex.x) + (t.x > apex.x ? 1 : -1) * 6 * Math.PI / 180;
      const half = 22 * Math.PI / 180;
      // Path relative to the apex, so the ping can scale out of it.
      const p1 = { x: FAN_R * Math.cos(c - half), y: FAN_R * Math.sin(c - half) };
      const p2 = { x: FAN_R * Math.cos(c + half), y: FAN_R * Math.sin(c + half) };
      return {
        apex,
        d: `M0,0 L${pt(p1)} A${FAN_R},${FAN_R} 0 0 1 ${pt(p2)} Z`
      };
    }
  }
};
</script>

<style scoped>
.ts-root { position: relative; width: 100%; overflow: hidden; background: #1c2833; }
.ts-stage { position: absolute; top: 0; left: 0; width: 460px; height: 270px; transform-origin: 0 0; font-family: Roboto, Arial, sans-serif; }
.ts-scene { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

.ts-score { position: absolute; top: 8px; left: 197px; display: flex; align-items: stretch; height: 22px; z-index: 2; }
.ts-score-pts { display: flex; align-items: center; gap: 12px; padding: 0 7px; background: #0b0b0b; color: #fff; font-weight: 700; font-size: var(--font-size-5xl); line-height: 1; }
.ts-score-status { display: flex; align-items: center; padding: 0 8px; background: rgba(12, 20, 18, 0.72); color: #e9e33b; font-weight: 700; font-size: var(--font-size-2xl); white-space: nowrap; }

.ts-label { position: absolute; padding: 4px 8px; border-radius: 5px; color: #fff; font-weight: 700; font-size: var(--font-size-3xl); line-height: 1.2; white-space: nowrap; max-width: 150px; overflow: hidden; text-overflow: ellipsis; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35); z-index: 2; }
.ts-label--home { top: 42px; left: 30px; background: #2d5fae; transition: top 0.5s ease; }
.ts-label--away { top: 87px; right: 42px; background: #1c8a8e; transition: top 0.5s ease; }
/* Moved down, below the court's middle, when the ball would sit under it. */
.ts-label--home.is-low, .ts-label--away.is-low { top: 150px; }

.ts-serving { position: absolute; left: 0; right: 0; top: 235px; display: flex; justify-content: center; align-items: center; gap: 7px; font-size: var(--font-size-3xl); z-index: 2; }
.ts-serving-label { color: #dbe7f1; font-weight: 600; }
.ts-serving-name { color: #fff; font-weight: 500; }
.ts-serving-bar { width: 2px; height: 15px; }
.ts-serving-bar.is-home { background: #3a6fd0; }
.ts-serving-bar.is-away { background: #1fb2b7; }

/* Idle: the server bounces the ball before serving. */
.ts-ball-idle { animation: ts-dribble 1.1s ease-in infinite; }
.ts-ball-idle-shadow { animation: ts-dribble-shadow 1.1s ease-in infinite; transform-box: fill-box; transform-origin: 50% 50%; }
@keyframes ts-dribble { 0%, 100% { transform: translateY(-9px); animation-timing-function: ease-in; } 50% { transform: translateY(0); animation-timing-function: ease-out; } }
@keyframes ts-dribble-shadow { 0%, 100% { transform: scale(0.6); opacity: 0.6; } 50% { transform: scale(1); opacity: 1; } }

/* Bounce mark left on the court. */
.ts-bounce { fill: none; stroke: rgba(255, 255, 255, 0.8); stroke-width: 1.2; transform-box: fill-box; transform-origin: 50% 50%; animation: ts-bounce 0.7s ease-out forwards; }
@keyframes ts-bounce { from { transform: scale(0.3); opacity: 1; } to { transform: scale(1.6); opacity: 0; } }

/* Score box: the number that just changed pops. */
.ts-score-pts .is-pop { display: inline-block; color: #e9e33b; animation: ts-pop 0.9s cubic-bezier(.2, 1.6, .4, 1) forwards; }
@keyframes ts-pop { 0% { transform: scale(0.4); } 40% { transform: scale(1.5); } 100% { transform: scale(1); color: #fff; } }

/* Point / game overlay. */
.ts-point-overlay {
  position: absolute; inset: 0; z-index: 5; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
  background: radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0.7) 100%);
  pointer-events: none;
}
.ts-point-overlay::before {
  content: ''; position: absolute; left: 0; right: 0; top: 50%; height: 96px; transform: translateY(-50%) skewY(-4deg);
  background: linear-gradient(90deg, transparent 0%, var(--side-color) 20%, var(--side-color) 80%, transparent 100%); opacity: 0.85;
  animation: ts-band 0.5s cubic-bezier(.2, .9, .3, 1) both;
}
.ts-point-title { position: relative; color: #fff; font-size: var(--font-size-17xl); font-weight: 900; font-style: italic; text-transform: uppercase; text-shadow: 0 3px 12px rgba(0, 0, 0, 0.55); animation: ts-title 0.6s cubic-bezier(.2, 1.5, .4, 1) 0.1s both; }
.ts-point-sub { position: relative; padding: 1px 10px; border-radius: 4px; background: #e9e33b; color: #0b0b0b; font-size: var(--font-size-3xl); font-weight: 900; text-transform: uppercase; animation: ts-rise 0.4s ease-out 0.2s both; }
.ts-point-team { position: relative; color: #fff; font-size: var(--font-size-4xl); font-weight: 700; opacity: 0.95; animation: ts-rise 0.4s ease-out 0.3s both; }
.ts-point-score { position: relative; display: flex; gap: 8px; margin-top: 4px; padding: 3px 12px; background: #0b0b0b; border-radius: 4px; color: #fff; font-size: var(--font-size-7xl); font-weight: 800; animation: ts-rise 0.4s ease-out 0.45s both; }
.ts-point-score .is-won { display: inline-block; color: #e9e33b; animation: ts-pop 0.9s cubic-bezier(.2, 1.6, .4, 1) 0.7s both; }
.ts-point-sep { opacity: 0.6; }
@keyframes ts-band { from { transform: translateY(-50%) skewY(-4deg) scaleX(0); } to { transform: translateY(-50%) skewY(-4deg) scaleX(1); } }
@keyframes ts-title { from { transform: scale(2.4); opacity: 0; } to { transform: scale(1); opacity: 1; } }
@keyframes ts-rise { from { transform: translateY(10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.ts-overlay-enter-active { transition: opacity 0.2s; }
.ts-overlay-leave-active { transition: opacity 0.4s; }
.ts-overlay-enter, .ts-overlay-leave-to { opacity: 0; }

/* Match over. */
.ts-final { position: absolute; inset: 0; z-index: 6; display: flex; align-items: center; justify-content: center; background: rgba(5, 10, 16, 0.62); }
.ts-final-badge {
  display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 14px 34px 16px;
  background: linear-gradient(160deg, #1f2937 0%, #0b1220 100%); border: 2px solid #facc15; border-radius: 14px;
  box-shadow: 0 0 0 4px rgba(250, 204, 21, 0.15), 0 12px 30px rgba(0, 0, 0, 0.6);
  animation: ts-final-in 0.7s cubic-bezier(.2, 1.4, .4, 1) both;
}
.ts-final-icon { width: 40px; height: 40px; animation: ts-trophy 2s ease-in-out 0.7s infinite; }
.ts-final-title { color: #facc15; font-size: var(--font-size-10xl); font-weight: 900; text-transform: uppercase; white-space: nowrap; }
.ts-final-winner { color: #fff; font-size: var(--font-size-5xl); font-weight: 700; }
.ts-final-sets { display: flex; gap: 10px; margin-top: 2px; color: #cbd5e1; font-size: var(--font-size-3xl); font-weight: 700; }
@keyframes ts-final-in { from { transform: scale(0.3) rotate(-6deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
@keyframes ts-trophy { 0%, 100% { transform: translateY(0) rotate(0); } 25% { transform: translateY(-3px) rotate(-6deg); } 75% { transform: translateY(-3px) rotate(6deg); } }
.ts-final-enter-active { transition: opacity 0.3s; }
.ts-final-enter { opacity: 0; }
.arena-logo { opacity: 0.95; pointer-events: none; }
</style>
